import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Hand, AlertCircle, RefreshCw, Activity, Users } from 'lucide-react';
import { bus, EVENTS } from '../lib/eventBus';
import { VisionAgent } from '../agents/VisionAgent';
import { ContextAgent } from '../agents/ContextAgent';
import { VocalizerAgent } from '../agents/VocalizerAgent';
import Subtitles from '../components/Subtitles';
import VoiceWaves from '../components/VoiceWaves';
import Controls from '../components/Controls';
import ConversationHistory from '../components/ConversationHistory';
import DetectionStatus from '../components/DetectionStatus';
import RegionSelector from '../components/RegionSelector';
import ContextModeToggle from '../components/ContextModeToggle';
import CategoryFilter from '../components/CategoryFilter';
import AslCheatsheet from '../components/AslCheatsheet';
import { findRegion, DEFAULT_REGION } from '../lib/regions';
import { loadDictionary, filterByCategory } from '../lib/dictionaryLoader';
import { getSessionId } from '../lib/sessionManager';
import { postHistory, fetchHistory, deleteHistory } from '../lib/historyApi';

/**
 * Phase 4 — ASL Dictionary Expansion + Temporal Tracking (consumer / `/`).
 *
 * Phase 2 surfaces (RegionSelector + ContextModeToggle) are HIDDEN behind
 * ENABLE_GLOBAL_MODE = false. The components, lib/regions, lib/dictionaryLoader
 * region cache and backend MX/PA/BR/IN endpoints all stay intact for easy
 * reactivation. Hardcoded region = US, tts = en-US.
 */
const ENABLE_GLOBAL_MODE = false;

const CATEGORY_KEY = 'signvoice:category';
const VALID_CATEGORIES = new Set(['all', 'everyday_basics', 'food_ordering']);

const readInitialCategory = () => {
  if (typeof localStorage === 'undefined') return 'all';
  const v = localStorage.getItem(CATEGORY_KEY);
  return VALID_CATEGORIES.has(v) ? v : 'all';
};

export default function MainApp() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const visionRef = useRef(null);
  const contextRef = useRef(null);
  const vocalRef = useRef(null);

  // ----- app state -----
  const [appState, setAppState] = useState('idle'); // idle | preparing | running | paused | error
  const [muted, setMuted] = useState(false);
  const [fps, setFps] = useState(0);
  const [candidate, setCandidate] = useState(null);
  const [currentSubtitle, setCurrentSubtitle] = useState('');
  const [history, setHistory] = useState([]);
  const [speaking, setSpeaking] = useState(false);
  const [camError, setCamError] = useState('');
  const [handStatus, setHandStatus] = useState('idle');
  const [flash, setFlash] = useState(null);
  const [handCount, setHandCount] = useState(0);
  const [trackingMode, setTrackingMode] = useState('dual'); // 'dual' | 'single' (after auto-degrade)

  // ----- ASL-only dictionary + category filter -----
  const [category, setCategory] = useState(readInitialCategory);
  const [dictionary, setDictionary] = useState({
    region: 'US',
    signLang: 'ASL',
    ttsLang: 'en-US',
    entries: [],
  });
  const [sessionId] = useState(() => getSessionId());

  // Refs mirror current state so bus callbacks (subscribed once) see fresh values.
  const flashTimerRef = useRef(null);
  const categoryRef = useRef(category);
  const dictRef = useRef(dictionary);
  useEffect(() => { categoryRef.current = category; }, [category]);
  useEffect(() => { dictRef.current = dictionary; }, [dictionary]);

  // ----- mount agents once -----
  useEffect(() => {
    const vision = new VisionAgent();
    const ctx = new ContextAgent();
    const vocal = new VocalizerAgent();
    visionRef.current = vision;
    contextRef.current = ctx;
    vocalRef.current = vocal;

    ctx.setCategory(category);
    vocal.setLang('en-US'); // Phase 4 — ASL-only, hardcoded

    vision.attach(videoRef.current, canvasRef.current);
    vision.init().catch((err) => {
      // eslint-disable-next-line no-console
      console.debug('[MainApp] MediaPipe init failed:', String(err));
    });

    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      window.__SV_BUS__ = bus;
      window.__SV_EVENTS__ = EVENTS;
    }

    const unsubs = [
      bus.on(EVENTS.GESTURE_CANDIDATE, (c) => setCandidate(c)),
      bus.on(EVENTS.PHRASE_READY, ({ text, gesture }) => {
        setCurrentSubtitle(text);
        const ts = Date.now();
        const localId = `${ts}-${Math.random().toString(36).slice(2, 7)}`;
        setHistory((h) => [...h, { text, ts, id: localId, gestureId: gesture }]);
        postHistory({
          session_id: sessionId,
          region: 'US',
          context_mode: 'general', // legacy backend field — keep "general"
          gesture_id: gesture,
          word: text,
          language: dictRef.current?.ttsLang || 'en-US',
        }).catch(() => { /* offline OK */ });
      }),
      bus.on(EVENTS.SPEAKING_START, () => setSpeaking(true)),
      bus.on(EVENTS.SPEAKING_END, () => setSpeaking(false)),
      bus.on(EVENTS.FPS, (n) => setFps(n)),
      bus.on(EVENTS.HAND_STATUS, (payload) => {
        if (!payload) return;
        setHandStatus(payload.status);
        if (payload.status === 'recognized' && payload.word) {
          setFlash({ gesture: payload.gesture, word: payload.word.toUpperCase() });
          if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
          flashTimerRef.current = setTimeout(() => setFlash(null), 1000);
        }
      }),
      bus.on(EVENTS.HANDS_COUNT, (n) => setHandCount(n || 0)),
      bus.on(EVENTS.TRACKING_DEGRADED, ({ to, measuredFps }) => {
        setTrackingMode(to === 1 ? 'single' : 'dual');
        // eslint-disable-next-line no-console
        console.log(`[MainApp] tracking auto-degraded → single hand (fps=${measuredFps})`);
      }),
    ];

    fetchHistory(sessionId, 200)
      .then((items) => {
        if (!Array.isArray(items) || items.length === 0) return;
        const asc = [...items]
          .reverse()
          .map((it) => ({
            text: it.word,
            ts: new Date(it.timestamp).getTime() || Date.now(),
            id: it.id,
            gestureId: it.gesture_id,
          }));
        setHistory(asc);
      })
      .catch(() => { /* OK if backend is down */ });

    return () => {
      unsubs.forEach((u) => u());
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      vision.destroy();
      ctx.destroy();
      vocal.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- load ASL dictionary on mount -----
  useEffect(() => {
    let cancelled = false;
    loadDictionary(DEFAULT_REGION)
      .then((data) => {
        if (cancelled) return;
        setDictionary(data);
        contextRef.current?.setDictionary(data.entries);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.debug('[MainApp] loadDictionary failed:', String(err));
      });
    return () => { cancelled = true; };
  }, []);

  // ----- apply category filter -----
  useEffect(() => {
    localStorage.setItem(CATEGORY_KEY, category);
    contextRef.current?.setCategory(category);
  }, [category]);

  // ----- handlers -----
  const handleStart = useCallback(async () => {
    setCamError('');
    setAppState('preparing');
    try {
      vocalRef.current?.unlock();
      await visionRef.current.startCamera();
      visionRef.current.start();
      setAppState('running');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug('[MainApp] startCamera failed:', String(err));
      const denied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      const notFound = err?.name === 'NotFoundError';
      setCamError(
        denied
          ? 'Camera permission denied. Enable it in your browser settings.'
          : notFound
            ? 'No camera was found.'
            : err?.message || 'Could not start the camera.',
      );
      setAppState('error');
    }
  }, []);

  const handlePauseResume = useCallback(() => {
    const v = visionRef.current;
    if (!v) return;
    if (appState === 'running') {
      v.pause();
      setAppState('paused');
    } else if (appState === 'paused') {
      v.start();
      setAppState('running');
    }
  }, [appState]);

  const handleToggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      vocalRef.current?.setMuted(next);
      return next;
    });
  }, []);

  const handleClearHistory = useCallback(() => {
    setHistory([]);
    setCurrentSubtitle('');
    deleteHistory(sessionId).catch(() => { /* offline OK */ });
  }, [sessionId]);

  const isLive = appState === 'running' || appState === 'paused';
  const region = findRegion(DEFAULT_REGION);
  const activeEntries = filterByCategory(dictionary.entries, category);
  // Only detectable signs (static + temporal) — used for the "X active signs" pill.
  const detectableEntries = activeEntries.filter(
    (e) => e.detection_status === 'static' || e.detection_status === 'temporal',
  );

  return (
    <div className="sv-shell relative" data-testid="main-app">
      <div className="sv-grid-overlay" />

      {/* HEADER */}
      <header className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8 pt-6 sm:pt-8 pb-4 flex flex-wrap items-center gap-3">
        <div className="sv-logo flex-shrink-0" data-testid="sv-logo">
          <span className="sv-logo-mark">
            <Hand className="w-4 h-4 text-white" strokeWidth={2.5} />
          </span>
          <span>SignVoice</span>
          <span className="sv-phase ml-2 hidden sm:inline">PHASE 4 · ASL DICTIONARY</span>
        </div>

        {ENABLE_GLOBAL_MODE && (
          <div className="flex-1 flex justify-center min-w-0 order-3 sm:order-2 w-full sm:w-auto" data-testid="global-region-row">
            <RegionSelector value={region.code} onChange={() => { /* hidden in Phase 4 */ }} />
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto flex-shrink-0 order-2 sm:order-3">
          <span
            className={`sv-pill ${appState === 'running' ? 'sv-pill-ok' : appState === 'error' ? 'sv-pill-warn' : 'sv-pill-on'}`}
            data-testid="app-state-pill"
          >
            ● {appState === 'running' ? 'Live' : appState === 'paused' ? 'Paused' : appState === 'preparing' ? 'Starting…' : appState === 'error' ? 'Error' : 'Stopped'}
          </span>
          <a href="/poc" className="sv-pill" data-testid="link-to-poc" title="Diagnostics page">
            <Activity className="w-3 h-3" /> Debug
          </a>
        </div>
      </header>

      {/* FILTER ROW — Phase 4 CategoryFilter (replaces ContextModeToggle visually) */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8 pb-3 flex items-center gap-3 flex-wrap" data-testid="filter-row">
        <CategoryFilter value={category} onChange={setCategory} />
        <span className="sv-pill sv-pill-on" data-testid="active-count-pill">
          ● {detectableEntries.length} detectable
        </span>
        <span className="sv-pill" data-testid="active-sign-lang-pill">ASL</span>
        <span className="sv-pill" data-testid="active-tts-lang-pill">TTS · en-US</span>
        {ENABLE_GLOBAL_MODE && (
          <span data-testid="hidden-context-toggle">
            <ContextModeToggle value="general" onChange={() => { /* hidden */ }} />
          </span>
        )}
      </div>

      {/* MAIN GRID */}
      <main
        className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8 pb-10 grid gap-5 lg:gap-7"
        style={{ gridTemplateColumns: '1fr' }}
        data-testid="main-grid"
      >
        <div className="sv-app-grid">
          {/* CAMERA COLUMN */}
          <section className="flex flex-col gap-4" data-testid="camera-column">
            <div className="sv-card p-3 sm:p-4">
              <div className="sv-cam-frame" data-testid="cam-frame">
                <video ref={videoRef} playsInline muted autoPlay data-testid="main-video" />
                <canvas ref={canvasRef} data-testid="main-canvas" />

                <div className="sv-hud sv-hud-tr">
                  <div className="sv-fps" data-testid="main-fps">{fps} FPS</div>
                </div>

                <div className="sv-hud sv-hud-tl">
                  <div className={`sv-detect-badge ${candidate ? 'sv-detect-on' : ''}`} data-testid="main-candidate-badge">
                    {candidate ? (
                      <>
                        <span style={{ color: 'var(--sv-violet-bright)' }}>●</span>
                        <span>
                          {dictionary.entries.find((d) => d.id === candidate.id)?.word || candidate.id}
                          <span className="ml-2 mono opacity-70" style={{ fontSize: '0.72rem' }}>
                            {Math.round(candidate.confidence * 100)}%
                          </span>
                        </span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--sv-text-dim)' }}>Waiting for sign…</span>
                    )}
                  </div>
                  {handCount === 2 && (
                    <div className="sv-dual-hands-badge" data-testid="dual-hands-badge" aria-label="Two hands detected">
                      <Users className="w-3 h-3" strokeWidth={2.5} />
                      <span>Dual hands</span>
                    </div>
                  )}
                </div>

                {(appState === 'idle' || appState === 'preparing' || appState === 'error') && (
                  <div
                    className="absolute inset-0 z-10 flex items-center justify-center text-center px-6"
                    style={{ background: 'rgba(10,15,37,0.88)', backdropFilter: 'blur(10px)' }}
                    data-testid="cam-overlay"
                  >
                    <div className="max-w-sm">
                      {appState === 'idle' && (
                        <>
                          <div className="mb-5">
                            <div className="sv-logo-mark mx-auto" style={{ width: 56, height: 56 }}>
                              <Hand className="w-7 h-7 text-white" strokeWidth={2.4} />
                            </div>
                          </div>
                          <h3 className="text-2xl font-bold mb-2">Ready to Start</h3>
                          <p className="text-sm mb-6" style={{ color: 'var(--sv-text-dim)' }}>
                            Touch to activate your camera and begin translating signs to voice.
                          </p>
                          <button className="sv-btn-primary" onClick={handleStart} data-testid="start-btn">
                            <Hand className="w-5 h-5" /> Start
                          </button>
                        </>
                      )}
                      {appState === 'preparing' && (
                        <>
                          <Activity className="w-8 h-8 mx-auto mb-3 animate-pulse" style={{ color: 'var(--sv-violet-bright)' }} />
                          <p className="font-semibold">Starting camera…</p>
                          <p className="text-sm mt-1" style={{ color: 'var(--sv-text-dim)' }}>Accept the browser permission prompt.</p>
                        </>
                      )}
                      {appState === 'error' && (
                        <>
                          <AlertCircle className="w-8 h-8 mx-auto mb-3" style={{ color: '#FCA5A5' }} />
                          <p className="font-semibold mb-1" data-testid="cam-error-title">Could not start camera</p>
                          <p className="text-sm mb-4" style={{ color: 'var(--sv-text-dim)' }}>{camError}</p>
                          <button className="sv-btn-ghost" onClick={handleStart} data-testid="cam-retry-btn">
                            <RefreshCw className="w-4 h-4" /> Retry
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3 px-1">
                <span
                  className={`sv-pill ${trackingMode === 'dual' ? 'sv-pill-on' : ''}`}
                  data-testid="hands-pill"
                  data-tracking-mode={trackingMode}
                  title={trackingMode === 'single' ? 'Auto-degraded to single hand due to low FPS' : 'Dual-hand tracking active'}
                >
                  Hand tracking · {trackingMode === 'dual' ? 'Dual' : 'Single'}
                </span>
                <span className="sv-pill" data-testid="dict-size-pill">
                  {detectableEntries.length} of {activeEntries.length} detectable
                </span>
              </div>
            </div>

            {/* Phase 4 Cheatsheet — 2 collapsible categories with badges */}
            <div className="hidden sm:block">
              <AslCheatsheet entries={activeEntries} category={category} />
            </div>
          </section>

          {/* CONVERSATION COLUMN */}
          <aside className="flex flex-col gap-4" data-testid="conversation-column">
            <div className="sv-card p-5 sm:p-6 sv-subtitle-block" data-testid="subtitle-card">
              <div className="flex items-center justify-between mb-3">
                <p className="sv-eyebrow">Subtitles</p>
                <VoiceWaves active={speaking} />
              </div>
              <DetectionStatus status={appState === 'running' ? handStatus : 'idle'} />
              <Subtitles text={currentSubtitle} active={isLive} speaking={speaking} flash={flash} />
            </div>

            <Controls
              appState={appState}
              muted={muted}
              onPauseResume={handlePauseResume}
              onToggleMute={handleToggleMute}
              onClear={handleClearHistory}
              hasHistory={history.length > 0}
            />

            <ConversationHistory items={history} onClear={handleClearHistory} />
          </aside>
        </div>
      </main>

      <footer
        className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8 pb-8 text-xs"
        style={{ color: 'var(--sv-text-muted)' }}
        data-testid="main-footer"
      >
        SignVoice · {dictionary.entries.length} signs · ASL ·{' '}
        <a className="underline hover:text-white" href={`${process.env.REACT_APP_BACKEND_URL}/api/dictionary`} target="_blank" rel="noopener noreferrer">
          /api/dictionary
        </a>{' '}
        · Diagnostics at{' '}
        <a className="underline hover:text-white" href="/poc">/poc</a>
        <span aria-hidden="true"> · </span>
        <a className="underline hover:text-white" href="/privacy" data-testid="footer-privacy">Privacy</a>
        <span aria-hidden="true"> · </span>
        <a className="underline hover:text-white" href="/terms" data-testid="footer-terms">Terms</a>
      </footer>
    </div>
  );
}
